import React, {
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { AssetContext } from "../contexts/AssetListContext.js";
import PdfViewerModal from "./PdfViewerModal.js";
import filterIcon from "../assets/icons/down_arrow.svg";
import "../css/DefectsPage.css";
import "../css/WorkspaceTabs.css";
import WorkspaceYearSelect from "./WorkspaceYearSelect.js";
import { riskColorMap } from "../utils/riskConfig";
import { getClosureReportUrl } from "../utils/mediaUrls.js";
import { useLocalStorage } from "../hooks/useStorage.js";
import {
    exportToExcel,
    exportToPdf,
    exportToDocx,
} from "../utils/exportDefects.js";
import { getDefectColumnValue } from "../utils/defectColumnValues.js";

/** Excel headers that may be absent; rows from sheet_to_json omit these keys entirely. */
const OPTIONAL_DEFECT_DATA_FIELDS = ["severity_level", "risk_score"];

function defectDatasetHasField(defects, field) {
    if (!Array.isArray(defects) || defects.length === 0) return false;
    return defects.some((row) =>
        Object.prototype.hasOwnProperty.call(row, field),
    );
}

// Column shape: { field, label, visible, showFilter, cellClassName?, valueGetter? }
// Optional `cellClassName` is applied to body <td> only (not header cells).
// Optional `valueGetter` references a serializable getter key from defectColumnValues.
const DEFAULT_DEFECT_TABLE_COLUMNS = [
    {
        field: "asset_name",
        label: "Asset",
        visible: true,
        showFilter: true,
    },
    {
        field: "component",
        label: "Component",
        visible: true,
        showFilter: true,
    },
    {
        field: "defect_id",
        label: "Defect",
        visible: true,
        showFilter: true,
    },
    {
        field: "Status",
        label: "Status",
        visible: true,
        showFilter: true,
        valueGetter: "closureStatus",
    },
    {
        field: "defect_type",
        label: "Defect Type",
        visible: true,
        showFilter: true,
    },
    {
        field: "issue_description",
        label: "Issue Description",
        visible: true,
        showFilter: false,
    },
    {
        field: "severity_level",
        label: "Severity Level",
        visible: true,
        showFilter: true,
    },
    {
        field: "risk_rate",
        label: "Risk Level",
        visible: true,
        showFilter: true,
    },
    {
        field: "risk_score",
        label: "Risk Score",
        visible: true,
        showFilter: false,
        cellClassName: "defects-col-cell-center",
    },
    {
        field: "remedial action description",
        label: "Remedial Action Description",
        visible: true,
        showFilter: true,
    },
    {
        field: "Task type",
        label: "Task Type",
        visible: true,
        showFilter: true,
    },
    {
        field: "Cost ($ AUD)",
        label: "Cost",
        visible: true,
        showFilter: false,
        cellClassName: "defects-col-cell-center",
    },
    {
        field: "remediation timeframe",
        label: "Remediation Timeframe",
        visible: true,
        showFilter: true,
    },
    {
        field: "repair_ref",
        label: "Remedial Procedure",
        visible: true,
        showFilter: false,
    },
    {
        field: "closure_report",
        label: "Closure Report",
        visible: true,
        showFilter: false,
    },
    {
        field: "Commentary",
        label: "Commentary",
        visible: true,
        showFilter: true,
    },
    {
        field: "Work Order N°/Notification N°",
        label: "Work Order N°/Notification N°",
        visible: true,
        showFilter: true,
    },
    {
        field: "Job Status",
        label: "Job Status",
        visible: true,
        showFilter: true,
    },
    {
        field: "Job Completion Rate",
        label: "Job Completion Rate",
        visible: true,
        showFilter: false,
        cellClassName: "defects-col-cell-center",
    },
];

/**
 * Keeps column list in sync with the loaded sheet: optional fields only appear
 * when at least one row includes that key. Preserves `visible` from previous state.
 */
function mergeColumnsWithDataset(defects, previousColumns) {
    const hasSeverity = defectDatasetHasField(defects, "severity_level");
    const hasRiskScore = defectDatasetHasField(defects, "risk_score");
    const template = DEFAULT_DEFECT_TABLE_COLUMNS.filter(
        (col) =>
            (col.field !== "severity_level" || hasSeverity) &&
            (col.field !== "risk_score" || hasRiskScore),
    );
    const prevByField = Object.fromEntries(
        (previousColumns || []).map((c) => [c.field, c]),
    );
    return template.map((col) => {
        const prev = prevByField[col.field];
        if (!prev) return { ...col };
        return { ...col, visible: prev.visible };
    });
}

// Mapping for job status (background + foreground for readable contrast)
const JOB_STATUS_CONFIG = {
    "Not Status Set": { backgroundColor: "#F7F7F7", color: "#222222" },
    "Not Actioned": { backgroundColor: "#B11C03", color: "#ffffff" },
    "Notification Raised": { backgroundColor: "#E86A02", color: "#ffffff" },
    "Waiting on Information / Engineering": {
        backgroundColor: "#FE9844",
        color: "#222222",
    },
    "Work Order Created": { backgroundColor: "#DAF2D0", color: "#222222" },
    "Planning in Progress": { backgroundColor: "#B5E6A2", color: "#222222" },
    "Work Planned": { backgroundColor: "#8ED973", color: "#222222" },
    "Work Completed": { backgroundColor: "#4EA42C", color: "#222222" },
};

/** @param {string | undefined} status */
function getJobStatusCellStyle(status) {
    if (!status) {
        return { backgroundColor: "transparent", color: "#757575" };
    }
    const cfg = JOB_STATUS_CONFIG[status];
    if (cfg) {
        return { backgroundColor: cfg.backgroundColor, color: cfg.color };
    }
    return { backgroundColor: "transparent", color: "#222222" };
}

// Mapping for job completion rates
const JOB_COMPLETION_RATES = {
    "Not Status Set": "0%",
    "Not Actioned": "0%",
    "Notification Raised": "10%",
    "Waiting on Information / Engineering": "20%",
    "Work Order Created": "30%",
    "Planning in Progress": "40%",
    "Work Planned": "50%",
    "Work Completed": "100%",
};

// Custom sort order for Job Status (index determines priority)
const JOB_STATUS_ORDER = [
    "Not Status Set",
    "Not Actioned",
    "Notification Raised",
    "Waiting on Information / Engineering",
    "Work Order Created",
    "Planning in Progress",
    "Work Planned",
    "Work Completed",
];

/**
 * Header cell: column title + Excel-style menu (sort + optional value filter).
 * Defined at module scope so React does not remount every parent render.
 */
function DefectsColumnHeaderMenu({
    column,
    activeMenuField,
    onToggleMenu,
    filters,
    sortConfig,
    onApplySort,
    onClearSort,
    getUniqueValues,
    onApplyFilterValues,
}) {
    const [searchText, setSearchText] = useState("");
    const [draftSelectedValues, setDraftSelectedValues] = useState([]);
    const [popupAlignLeft, setPopupAlignLeft] = useState(false);
    const wrapRef = useRef(null);
    const field = column.field;
    const options = column.showFilter ? getUniqueValues(field) : [];
    const committedValues = filters[field] || [];
    const isOpen = activeMenuField === field;
    const isSortedHere = sortConfig.key === field && sortConfig.direction;
    const filterBadge = committedValues.length;

    useEffect(() => {
        if (!isOpen) return;
        setDraftSelectedValues([...(filters[field] || [])]);
        setSearchText("");
        // Intentionally only when this column's menu opens — not when `filters` updates mid-session
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, field]);

    useEffect(() => {
        if (!isOpen || !wrapRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        const popupMinWidth = 240;
        // right:0 means the popup's right edge aligns with the wrap's right edge, extending left.
        // If that would go off-screen, flip to left-aligned instead.
        setPopupAlignLeft(rect.right - popupMinWidth < 0);
    }, [isOpen]);

    return (
        <th scope="col" style={isOpen ? { zIndex: 3 } : undefined}>
            <div className="defects-header-cell-inner">
                <span className="defects-header-label">
                    {column.label}
                    {isSortedHere && (
                        <span className="defects-header-sort-badge" aria-hidden>
                            {sortConfig.direction === "ascending" ? " ▲" : " ▼"}
                        </span>
                    )}
                </span>
                <div className="defects-header-menu-wrap" ref={wrapRef}>
                    <button
                        type="button"
                        className="filter-button defects-filter-trigger defects-header-filter-btn"
                        aria-expanded={isOpen}
                        aria-haspopup="dialog"
                        aria-label={`Sort and filter: ${column.label}`}
                        title="Sort and filter"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleMenu(field);
                        }}
                    >
                        <img src={filterIcon} alt="" />
                        {filterBadge > 0 && (
                            <span className="filter-count">{filterBadge}</span>
                        )}
                    </button>

                    {isOpen && (
                        <div
                            className="filter-dropdown defects-column-menu-popup"
                            role="dialog"
                            aria-label={`${column.label} options`}
                            onClick={(e) => e.stopPropagation()}
                            style={popupAlignLeft ? { right: "auto", left: 0 } : undefined}
                        >
                            <div className="defects-menu-section defects-menu-section--sort">
                                <div className="defects-menu-section-title">
                                    Sort
                                </div>
                                <button
                                    type="button"
                                    className="defects-menu-row"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onApplySort(field, "ascending");
                                    }}
                                >
                                    Ascending
                                </button>
                                <button
                                    type="button"
                                    className="defects-menu-row"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onApplySort(field, "descending");
                                    }}
                                >
                                    Descending
                                </button>
                                <button
                                    type="button"
                                    className="defects-menu-row defects-menu-row-muted"
                                    disabled={!isSortedHere}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onClearSort(field);
                                    }}
                                >
                                    Clear sort
                                </button>
                            </div>

                            {column.showFilter && (
                                <>
                                    <div
                                        className="defects-menu-divider"
                                        role="separator"
                                    />
                                    <div className="defects-menu-section defects-menu-section--filter">
                                        <div className="defects-menu-section-title">
                                            Filter by values
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="Search values…"
                                            value={searchText}
                                            onChange={(e) =>
                                                setSearchText(e.target.value)
                                            }
                                            className="filter-search-input"
                                        />
                                        <button
                                            type="button"
                                            className="clear-filter-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onApplyFilterValues(field, []);
                                            }}
                                        >
                                            Clear selection
                                        </button>
                                        <div className="select-options">
                                            {options
                                                .filter((option) =>
                                                    String(option)
                                                        .toLowerCase()
                                                        .includes(
                                                            searchText.toLowerCase(),
                                                        ),
                                                )
                                                .map((option, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        className={`filter-option ${draftSelectedValues.includes(option) ? "selected-option" : ""}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDraftSelectedValues(
                                                                (prev) => {
                                                                    const i =
                                                                        prev.indexOf(
                                                                            option,
                                                                        );
                                                                    if (
                                                                        i === -1
                                                                    ) {
                                                                        return [
                                                                            ...prev,
                                                                            option,
                                                                        ];
                                                                    }
                                                                    return prev.filter(
                                                                        (
                                                                            _,
                                                                            j,
                                                                        ) =>
                                                                            j !==
                                                                            i,
                                                                    );
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        <span className="option-text">
                                                            {option}
                                                        </span>
                                                    </button>
                                                ))}
                                        </div>
                                        <button
                                            type="button"
                                            className="defects-filter-apply-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onApplyFilterValues(
                                                    field,
                                                    draftSelectedValues,
                                                );
                                            }}
                                        >
                                            Apply filter
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </th>
    );
}

const DefectsPage = () => {
    const {
        defects,
        setDefects,
        saveDefectsToExcel = () => {},
        selectedYear,
        setSelectedYear,
        availableYears = [],
        isDefectsLoading = false,
    } = useContext(AssetContext);
    const navigate = useNavigate();

    const colors = riskColorMap;
    const [filters, setFilters] = useLocalStorage("tableFilters", {});
    const [filteredDefects, setFilteredDefects] = useState(defects);
    const [activeFilter, setActiveFilter] = useState(null);
    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [openTruncatedCellKey, setOpenTruncatedCellKey] = useState(null);
    const tableScrollRef = useRef(null);
    const statusDropdownRef = useRef(null);

    const [isPdfOpen, setIsPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    const openPdf = (url) => {
        setPdfUrl(url);
        setIsPdfOpen(true);
    };

    const closePdf = () => {
        setIsPdfOpen(false);
        setPdfUrl(null);
    };

    /** Skip save on blur after Escape cancel */
    const escapeCancelRef = useRef(false);

    const [columns, setColumns] = useLocalStorage(
        "tableColumns",
        DEFAULT_DEFECT_TABLE_COLUMNS,
    );

    const getColumnForField = useCallback(
        (field) =>
            columns.find((column) => column.field === field) || { field },
        [columns],
    );

    const getDefectValue = useCallback(
        (defect, field) =>
            getDefectColumnValue(defect, getColumnForField(field)),
        [getColumnForField],
    );

    const defectMatchesFilter = useCallback(
        (defect, key, values) => {
            if (!values || values.length === 0) return true;

            const defectValue = getDefectValue(defect, key);
            if (key === "Cost ($ AUD)") {
                return values.some(
                    (value) => parseFloat(defectValue) === parseFloat(value),
                );
            }

            const normalizedDefectValue = String(
                defectValue || "",
            ).toLowerCase();
            return values.some(
                (value) =>
                    String(value).toLowerCase() === normalizedDefectValue,
            );
        },
        [getDefectValue],
    );

    const defectMatchesFilters = useCallback(
        (defect, activeFilters = filters) =>
            Object.entries(activeFilters).every(([key, values]) =>
                defectMatchesFilter(defect, key, values),
            ),
        [defectMatchesFilter, filters],
    );

    useEffect(() => {
        const saved = sessionStorage.getItem("defectsTableScrollTop");
        if (saved && tableScrollRef.current) {
            tableScrollRef.current.scrollTop = parseInt(saved, 10);
            sessionStorage.removeItem("defectsTableScrollTop");
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                statusDropdownRef.current &&
                !statusDropdownRef.current.contains(event.target)
            ) {
                setEditingId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(".truncated-value-cell")) {
                setOpenTruncatedCellKey(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const ColumnVisibilityDropdown = () => (
        <div className="column-visibility-container">
            <button
                className="hide-show-button"
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
                <div className="button-content">
                    <h6 className="defects-column-toggle-title">
                        Hide/Show Column
                    </h6>
                    <img
                        src={filterIcon}
                        alt="Toggle columns"
                        className={`dropdown-arrow ${showColumnDropdown ? "rotated" : ""}`}
                    />
                </div>
            </button>

            {showColumnDropdown && (
                <div className="column-dropdown">
                    <div className="dropdown-content">
                        {columns.map((column, index) => (
                            <div key={index} className="column-option">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={column.visible}
                                        onChange={() =>
                                            toggleColumnVisibility(index)
                                        }
                                        className="checkbox-input"
                                    />
                                    <span className="checkbox-text">
                                        {column.label}
                                    </span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            const container = document.querySelector(
                ".column-visibility-container",
            );
            if (container && !container.contains(event.target)) {
                setShowColumnDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleColumnVisibility = (columnIndex) => {
        setColumns((prevColumns) =>
            prevColumns.map((column, index) =>
                index === columnIndex
                    ? { ...column, visible: !column.visible }
                    : column,
            ),
        );
    };

    const getUniqueValues = (field) => {
        const relevantFilters = Object.fromEntries(
            Object.entries(filters).filter(([key]) => key !== field),
        );

        const filteredForField = defects.filter((defect) =>
            defectMatchesFilters(defect, relevantFilters),
        );

        return [
            ...new Set(
                filteredForField.map((defect) => getDefectValue(defect, field)),
            ),
        ].filter(Boolean);
    };

    useEffect(() => {
        const filtered = defects.filter((defect) =>
            defectMatchesFilters(defect),
        );
        setFilteredDefects(filtered);
    }, [filters, defects, defectMatchesFilters]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                !event.target.closest(".filter-dropdown") &&
                !event.target.closest(".defects-filter-trigger")
            ) {
                setActiveFilter(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: "AUD",
        }).format(value);
    };

    const handleFilterClick = (field) => {
        setActiveFilter(activeFilter === field ? null : field);
    };

    const applyFilterValues = (field, values) => {
        setFilters((prev) => {
            const newFilters = { ...prev };
            if (!values || values.length === 0) {
                delete newFilters[field];
            } else {
                newFilters[field] = [...values];
            }
            return newFilters;
        });
        setActiveFilter(null);
    };

    const clearAllFilters = () => {
        setFilters({});
        setActiveFilter(null);
    };

    const [sortConfig, setSortConfig] = useLocalStorage("tableSortConfig", {
        key: null,
        direction: "ascending",
    });

    useEffect(() => {
        if (isDefectsLoading) return;

        setColumns((prev) => mergeColumnsWithDataset(defects, prev));

        setFilters((prev) => {
            let next = prev;
            for (const field of OPTIONAL_DEFECT_DATA_FIELDS) {
                if (defectDatasetHasField(defects, field)) continue;
                if (!(field in next)) continue;
                if (next === prev) next = { ...prev };
                delete next[field];
            }
            return next;
        });

        setSortConfig((prev) => {
            if (
                prev.key &&
                OPTIONAL_DEFECT_DATA_FIELDS.includes(prev.key) &&
                !defectDatasetHasField(defects, prev.key)
            ) {
                return { key: null, direction: "ascending" };
            }
            return prev;
        });
    }, [defects, isDefectsLoading, setColumns, setFilters, setSortConfig]);

    const sortedDefects = [...filteredDefects].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let valueA = getDefectValue(a, sortConfig.key);
        let valueB = getDefectValue(b, sortConfig.key);

        // Handle numeric sorting for Cost
        if (sortConfig.key === "Cost ($ AUD)") {
            valueA = parseFloat(valueA) || 0;
            valueB = parseFloat(valueB) || 0;
        }
        // Handle numeric sorting for Risk Score (avoid string order: 9, 29, 19)
        else if (sortConfig.key === "risk_score") {
            const isBlankA =
                valueA === null ||
                valueA === undefined ||
                valueA === "" ||
                Number.isNaN(Number(valueA));
            const isBlankB =
                valueB === null ||
                valueB === undefined ||
                valueB === "" ||
                Number.isNaN(Number(valueB));

            if (isBlankA && isBlankB) return 0;
            if (isBlankA) return 1;
            if (isBlankB) return -1;

            valueA = Number(valueA);
            valueB = Number(valueB);
        }
        // Handle custom sorting for Job Status based on workflow order
        else if (sortConfig.key === "Job Status") {
            const isBlankA = !valueA || valueA === "";
            const isBlankB = !valueB || valueB === "";

            // Blanks always go to the end, regardless of sort direction
            if (isBlankA && isBlankB) return 0;
            if (isBlankA) return 1;
            if (isBlankB) return -1;

            valueA = JOB_STATUS_ORDER.indexOf(valueA);
            valueB = JOB_STATUS_ORDER.indexOf(valueB);
            // Unknown status goes to end (but before blanks)
            if (valueA === -1) valueA = JOB_STATUS_ORDER.length;
            if (valueB === -1) valueB = JOB_STATUS_ORDER.length;
        }
        // Handle sorting for Job Completion Rate by percentage value
        else if (sortConfig.key === "Job Completion Rate") {
            const isBlankA = !valueA || valueA === "";
            const isBlankB = !valueB || valueB === "";

            // Blanks always go to the end, regardless of sort direction
            if (isBlankA && isBlankB) return 0;
            if (isBlankA) return 1;
            if (isBlankB) return -1;

            // Extract numeric percentage from the value (e.g., '10%' -> 10)
            valueA = parseInt(String(valueA).replace("%", ""), 10);
            valueB = parseInt(String(valueB).replace("%", ""), 10);
        } else {
            // Alphabetical sorting for text fields
            valueA = String(valueA || "").toLowerCase();
            valueB = String(valueB || "").toLowerCase();
        }

        if (valueA < valueB) {
            return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (valueA > valueB) {
            return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
    });

    const applyColumnSort = (field, direction) => {
        setSortConfig({ key: field, direction });
    };

    const clearColumnSort = (field) => {
        setSortConfig((prev) =>
            prev.key === field ? { key: null, direction: null } : prev,
        );
    };

    const renderHeader = (column) => (
        <DefectsColumnHeaderMenu
            key={column.field}
            column={column}
            activeMenuField={activeFilter}
            onToggleMenu={handleFilterClick}
            filters={filters}
            sortConfig={sortConfig}
            onApplySort={applyColumnSort}
            onClearSort={clearColumnSort}
            getUniqueValues={getUniqueValues}
            onApplyFilterValues={applyFilterValues}
        />
    );

    const handleEdit = async (defectId, field, currentValue) => {
        // Save previous edit if there was one
        if (editingId) {
            await handleSave(editingId.id, editingId.field);
        }
        setEditingId({ id: defectId, field });
        setEditValue(currentValue || "");
    };

    const handleStatusClick = (defectId, field, currentEditingId) => {
        // If there is already a cell being edited
        if (currentEditingId) {
            if (
                currentEditingId.id === defectId &&
                currentEditingId.field === field
            ) {
                setEditingId(null);
                return;
            }
            setEditingId({ id: defectId, field });
            return;
        }
        setEditingId({ id: defectId, field });
    };

    const handleSave = async (defectId, field) => {
        const updatedDefects = defects.map((defect) => {
            if (defect.defect_id === defectId) {
                return {
                    ...defect,
                    [field]: editValue,
                };
            }
            return defect;
        });

        if (setDefects) {
            setDefects(updatedDefects);
        }

        // Apply existing filters to the updated set of defects
        const filtered = updatedDefects.filter((defect) =>
            defectMatchesFilters(defect),
        );
        setFilteredDefects(filtered);

        const success = await saveDefectsToExcel(updatedDefects, selectedYear);
        if (success) {
            console.log("Defects saved successfully");
        } else {
            console.error("Error saving defects");
        }

        setEditingId(null);
    };

    const handleStatusSelect = async (defectId, status) => {
        const updatedDefects = defects.map((defect) => {
            if (defect.defect_id === defectId) {
                return {
                    ...defect,
                    "Job Status": status,
                    "Job Completion Rate": JOB_COMPLETION_RATES[status], // Automatically update completion rate
                };
            }
            return defect;
        });

        if (setDefects) {
            setDefects(updatedDefects);
        }

        setFilteredDefects(
            updatedDefects.filter((defect) => defectMatchesFilters(defect)),
        );

        const success = await saveDefectsToExcel(updatedDefects, selectedYear);
        if (success) {
            console.log("Status and completion rate updated successfully");
        } else {
            console.error("Error updating status and completion rate");
        }

        setEditingId(null);
    };

    const handleKeyPress = (e, defectId, field) => {
        if (e.key === "Enter") {
            handleSave(defectId, field);
        } else if (e.key === "Escape") {
            setEditingId(null);
        }
    };

    const StatusDropdown = ({ defectId, currentStatus }) => {
        const handleOptionClick = (e, status) => {
            e.stopPropagation();
            handleStatusSelect(defectId, status);
        };

        return (
            <div
                className="status-dropdown"
                onClick={(e) => e.stopPropagation()}
            >
                {Object.entries(JOB_STATUS_CONFIG).map(([status, cfg]) => (
                    <div
                        key={status}
                        className="status-option"
                        style={{
                            backgroundColor: cfg.backgroundColor,
                            color: cfg.color,
                        }}
                        onClick={(e) => handleOptionClick(e, status)}
                    >
                        {status}
                    </div>
                ))}
            </div>
        );
    };

    const renderCell = (defect, column) => {
        const isEditing =
            editingId &&
            editingId.id === defect.defect_id &&
            editingId.field === column.field;

        if (column.field === "component") {
            // Backward/forward compatibility if the dataset was renamed from `area` -> `component`.
            return defect.component || defect.area || "";
        }

        if (column.field === "Job Status") {
            const currentStatus = defect[column.field];
            const statusStyle = getJobStatusCellStyle(currentStatus);

            return (
                <div className="status-cell-container">
                    <div
                        className={`status-cell ${isEditing ? "active" : ""}`}
                        style={statusStyle}
                        onClick={(e) => {
                            handleStatusClick(
                                defect.defect_id,
                                column.field,
                                editingId,
                            );
                        }}
                    >
                        {currentStatus || "Select status"}
                    </div>
                    {isEditing && (
                        <StatusDropdown
                            defectId={defect.defect_id}
                            currentStatus={currentStatus}
                        />
                    )}
                </div>
            );
        }

        if (column.field === "Job Completion Rate") {
            const currentStatus = defect["Job Status"];
            const statusStyle = getJobStatusCellStyle(currentStatus);

            return (
                <div className="status-cell" style={statusStyle}>
                    {defect[column.field]}
                </div>
            );
        }

        if (column.field === "Commentary") {
            return isEditing ? (
                <div>
                    <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => {
                            if (escapeCancelRef.current) {
                                escapeCancelRef.current = false;
                                return;
                            }
                            handleSave(defect.defect_id, column.field);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                e.preventDefault();
                                escapeCancelRef.current = true;
                                setEditingId(null);
                                setEditValue("");
                                return;
                            }
                            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                                handleSave(defect.defect_id, column.field);
                            }
                        }}
                        className="editable-cell-textarea"
                        rows={4}
                        placeholder="Enter your comment…"
                        autoFocus
                        aria-label="Commentary"
                    />
                    <div className="editable-hint">
                        Ctrl+Enter to save · Esc to cancel
                    </div>
                </div>
            ) : (
                <div
                    className="editable-cell commentary"
                    style={{ color: defect[column.field] ? "black" : "gray" }}
                    title="Click to edit"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                        handleEdit(
                            defect.defect_id,
                            column.field,
                            defect[column.field],
                        )
                    }
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleEdit(
                                defect.defect_id,
                                column.field,
                                defect[column.field],
                            );
                        }
                    }}
                >
                    <span className="truncated-value-text">
                        {defect[column.field] || "Click to edit"}
                    </span>
                </div>
            );
        }

        if (
            ["issue_description", "remedial action description"].includes(
                column.field,
            )
        ) {
            const value = defect[column.field] || "";
            const cellKey = `${defect.defect_id}-${column.field}`;
            const isOpen = openTruncatedCellKey === cellKey;

            if (!value) {
                return <span style={{ color: "gray" }}>No description</span>;
            }

            return (
                <div className="truncated-value-cell">
                    <button
                        type="button"
                        className="truncated-value-trigger"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenTruncatedCellKey((prev) =>
                                prev === cellKey ? null : cellKey,
                            );
                        }}
                    >
                        <span className="truncated-value-text">{value}</span>
                    </button>
                    {isOpen && (
                        <div className="truncated-value-popover" role="tooltip">
                            {value}
                        </div>
                    )}
                </div>
            );
        } else if (column.field === "Work Order N°/Notification N°") {
            return isEditing ? (
                <textarea
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => {
                        if (escapeCancelRef.current) {
                            escapeCancelRef.current = false;
                            return;
                        }
                        handleSave(defect.defect_id, column.field);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            e.preventDefault();
                            escapeCancelRef.current = true;
                            setEditingId(null);
                            setEditValue("");
                            return;
                        }
                        handleKeyPress(e, defect.defect_id, column.field);
                    }}
                    className="editable-cell-input"
                    aria-label="Work order or notification number"
                    autoFocus
                />
            ) : (
                <div
                    className="editable-cell"
                    style={{ color: defect[column.field] ? "black" : "gray" }}
                    title="Click to edit"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                        handleEdit(
                            defect.defect_id,
                            column.field,
                            defect[column.field],
                        )
                    }
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleEdit(
                                defect.defect_id,
                                column.field,
                                defect[column.field],
                            );
                        }
                    }}
                >
                    <span className="truncated-value-text">
                        {defect[column.field] || "Click to edit"}
                    </span>
                </div>
            );
        }
        // Handle other special cell types as before
        if (column.field === "risk_rate") {
            return (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        flexWrap: "wrap",
                    }}
                >
                    <span
                        style={{
                            backgroundColor: colors[defect.risk_rate],
                            height: "15px",
                            width: "15px",
                            borderRadius: "4px",
                        }}
                    />
                    {defect[column.field]}
                </div>
            );
        }

        if (column.field === "repair_ref") {
            const value = defect[column.field];
            if (!value) {
                return <span style={{ color: "gray" }}>No Procedure</span>;
            }
            return (
                <div
                    style={{
                        color: "blue",
                        textDecoration: "underline",
                        cursor: "pointer",
                    }}
                    onClick={() =>
                        openPdf(
                            `/remedial_reports/${encodeURIComponent(value)}`,
                        )
                    }
                >
                    Show file
                </div>
            );
        }

        if (column.field === "closure_report") {
            const value = defect[column.field];
            if (!value) {
                return <span style={{ color: "gray" }}>No Closure</span>;
            }
            return (
                <div
                    style={{
                        color: "blue",
                        textDecoration: "underline",
                        cursor: "pointer",
                    }}
                    onClick={() => openPdf(getClosureReportUrl(defect))}
                >
                    Show file
                </div>
            );
        }

        if (column.field === "defect_id") {
            return (
                <div
                    style={{
                        color: "blue",
                        textDecoration: "underline",
                        cursor: "pointer",
                    }}
                    onClick={() => {
                        if (tableScrollRef.current) {
                            sessionStorage.setItem("defectsTableScrollTop", tableScrollRef.current.scrollTop);
                        }
                        navigate(`/defect-details/${defect.defect_id}`);
                    }}
                >
                    {defect[column.field]}
                </div>
            );
        }

        if (column.field === "defect_type") {
            return <span>{defect[column.field]}</span>;
        }
        if (column.field === "Cost ($ AUD)") {
            return formatCurrency(defect[column.field]);
        }
        return getDefectColumnValue(defect, column);
    };

    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const exportDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                exportDropdownRef.current &&
                !exportDropdownRef.current.contains(e.target)
            ) {
                setShowExportDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="cost-analysis-container remedial-actions-page">
            <div className="cost-analytics-title workspace-page-toolbar">
                <div className="defects-toolbar-row">
                    <WorkspaceYearSelect
                        id="defect-year-select"
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        availableYears={availableYears}
                        disabled={
                            availableYears.length === 0 || isDefectsLoading
                        }
                    />
                    <div
                        className="export-dropdown-container"
                        ref={exportDropdownRef}
                    >
                        <button
                            className="download-excel-button"
                            onClick={() => setShowExportDropdown((v) => !v)}
                        >
                            Export ▾
                        </button>
                        {showExportDropdown && (
                            <div className="export-dropdown-menu">
                                <button
                                    onClick={() => {
                                        exportToExcel(sortedDefects, columns);
                                        setShowExportDropdown(false);
                                    }}
                                >
                                    Excel
                                </button>
                                <button
                                    onClick={() => {
                                        exportToPdf(sortedDefects, columns);
                                        setShowExportDropdown(false);
                                    }}
                                >
                                    PDF
                                </button>
                                <button
                                    onClick={() => {
                                        exportToDocx(sortedDefects, columns);
                                        setShowExportDropdown(false);
                                    }}
                                >
                                    Word
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="defects-toolbar-columns-row">
                        <ColumnVisibilityDropdown />
                        <button
                            type="button"
                            className="defects-clear-all-filters-btn"
                            onClick={clearAllFilters}
                            disabled={Object.keys(filters).length === 0}
                        >
                            Clear filters
                        </button>
                    </div>
                </div>
            </div>

            <div className="defects-table-scroll" ref={tableScrollRef}>
                <table>
                    <thead>
                        <tr>
                            {columns
                                .filter((column) => column.visible)
                                .map((column) => renderHeader(column))}
                        </tr>
                    </thead>
                    <tbody>
                        {isDefectsLoading ? (
                            <tr>
                                <td
                                    colSpan={
                                        columns.filter(
                                            (column) => column.visible,
                                        ).length
                                    }
                                    style={{
                                        textAlign: "center",
                                        padding: "16px",
                                    }}
                                >
                                    Loading defect data...
                                </td>
                            </tr>
                        ) : (
                            sortedDefects.map((defect, index) => (
                                <tr key={index}>
                                    {columns
                                        .filter((column) => column.visible)
                                        .map((column) => (
                                            <td
                                                key={column.field}
                                                className={
                                                    column.cellClassName ||
                                                    undefined
                                                }
                                            >
                                                {renderCell(defect, column)}
                                            </td>
                                        ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <PdfViewerModal
                isOpen={isPdfOpen}
                onClose={closePdf}
                fileUrl={pdfUrl}
            />
        </div>
    );
};

export default DefectsPage;
